import { useRef, useState } from 'react';

import { useSortable } from '@dnd-kit/react/sortable';

import { ContentStep, Plan, PlanSection } from '@cfa/pathway-fe-shared/xp';
import { Card, Divider } from '@cfa/react-components';
import { Surface } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import { BaseReferenceDTO } from '@/services/content-api-types';
import { TaskProps } from '@/types/types';

import Constants from 'constants/index';

import { AddTask } from './AddTask';
import PlanStepContainer, { SectionFunctionProps } from './PlanStepContainer';
import SectionButtons from './SectionButtons';
import Title from './Title';

interface SectionProps {
  plan?: Plan;
  refetch?: () => void;
  section: PlanSection;
  taskUpdated?: boolean;
  isAllowedToEdit?: boolean;
  editSectionTitle?: (section: PlanSection) => boolean;
  renameSectionPopUp?: (sectionId: string) => void;
  deleteSectionPopUp?: (sectionId: string, sectionName: string) => void;
  onClickThreeDotMenuDeleteTask?: ({
    id,
    name,
    sectionId,
    type,
  }: SectionFunctionProps) => void;
  handleResourceOrQuizPreview?: (document: BaseReferenceDTO | string) => void;
  addProcedure?: (id?: string) => void;
  addQuiz?: () => void;
  onCancel?: () => void;
  onEdit?: (
    type: string,
    title: string | null,
    task: TaskProps,
    id: string,
  ) => void;
  handleRef?: React.Ref<HTMLElement>;
  setActiveSectionId?: (id: string) => void;
  isPreview?: boolean;
  language?: string;
  index: number;
  editProcedureResource?: ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => void;
  isReadOnly?: boolean;
  className?: string;
}

interface SortableSectionStepProps {
  step: ContentStep;
  index: number;
  sectionId: string;
  isReadOnly: boolean;
  isPreview?: boolean;
  isEditable: boolean;
  language?: string;
  onCancel?: () => void;
  onDelete?: (section: SectionFunctionProps) => void;
  onEdit?: (
    type: string,
    title: string | null,
    task: TaskProps,
    id: string,
  ) => void;
  editProcedureResource?: ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => void;
  handleResourceOrQuizPreview?: (document: BaseReferenceDTO | string) => void;
  setActiveSectionId?: (id: string) => void;
  taskUpdated: boolean;
}

const SortableSectionStep: React.FC<SortableSectionStepProps> = ({
  step,
  index,
  sectionId,
  isReadOnly,
  isPreview,
  isEditable,
  language,
  onCancel,
  onDelete,
  onEdit,
  editProcedureResource,
  handleResourceOrQuizPreview,
  setActiveSectionId,
  taskUpdated,
}) => {
  const [element, setElement] = useState<Element | null>(null);
  const handleRef = useRef<HTMLElement | null>(null);
  useSortable({
    id: step.id,
    index,
    type: 'step',
    accept: 'step',
    group: sectionId,
    disabled: isReadOnly || isPreview,
    element,
    handle: handleRef,
  });

  return (
    <div
      ref={setElement}
      className={cn('my-4 first:mt-0 last:mb-0')}
      onPointerEnter={() => setActiveSectionId?.(sectionId)}
    >
      <Surface className="pathway-surface">
        <PlanStepContainer
          handleRef={handleRef}
          handleResourceOrQuizPreview={handleResourceOrQuizPreview}
          isAllowedToEdit={isEditable}
          isPreview={isPreview}
          language={language}
          onCancel={onCancel}
          onDelete={onDelete}
          onEdit={onEdit}
          editProcedureResource={editProcedureResource}
          sectionId={sectionId}
          step={step}
          taskUpdated={taskUpdated}
          isReadOnly={isReadOnly}
        />
      </Surface>
    </div>
  );
};

const Section: React.FC<SectionProps> = ({
  isPreview,
  plan,
  section,
  isAllowedToEdit,
  editSectionTitle,
  renameSectionPopUp,
  deleteSectionPopUp,
  onClickThreeDotMenuDeleteTask,
  handleResourceOrQuizPreview,
  addProcedure,
  addQuiz,
  onCancel,
  onEdit,
  handleRef,
  setActiveSectionId,
  taskUpdated,
  language,
  index,
  editProcedureResource,
  isReadOnly = false,
  className,
}) => {
  const [showAddTask, setShowAddTask] = useState(false);
  const sectionId = section.id;
  const isEditable = isAllowedToEdit ?? false;
  const isTaskUpdated = taskUpdated ?? false;

  return (
    <Card
      data-testid={`SectionCard-${index}`}
      elevation={1}
      className={cn('relative mx-0 p-4', className)}
    >
      <Title
        deleteSectionPopUp={
          deleteSectionPopUp as (sectionId: string, sectionName: string) => void
        }
        editSectionTitle={editSectionTitle as (title: PlanSection) => boolean}
        handleRef={handleRef}
        index={index}
        isAllowedToEdit={isEditable}
        isPreview={isPreview}
        onCancel={onCancel as () => void}
        onEdit={updatedSectionTitle =>
          onEdit?.(
            Constants.TRAINING_MENU_OPTIONS.SECTION,
            updatedSectionTitle,
            { id: '', name: '', note: '', url: '' },
            sectionId,
          )
        }
        renameSectionPopUp={renameSectionPopUp}
        section={section}
        isReadOnly={isReadOnly}
      />

      <Divider
        className={cn('mx-0! mb-4!', isAllowedToEdit ? 'mt-2!' : 'mt-4!')}
        variant="fullLength"
      />

      <div className="w-full">
        {section.steps?.map((step, idx) =>
          isPreview ? (
            <div key={step.id} className={cn('my-4 first:mt-0 last:mb-0')}>
              <Surface className="pathway-surface">
                <PlanStepContainer
                  handleResourceOrQuizPreview={handleResourceOrQuizPreview}
                  isAllowedToEdit={isEditable}
                  isPreview={isPreview}
                  language={language}
                  onCancel={onCancel}
                  onDelete={onClickThreeDotMenuDeleteTask}
                  onEdit={onEdit}
                  editProcedureResource={editProcedureResource}
                  sectionId={sectionId}
                  step={step}
                  taskUpdated={isTaskUpdated}
                  isReadOnly={isReadOnly}
                />
              </Surface>
            </div>
          ) : (
            <SortableSectionStep
              key={step.id}
              step={step}
              index={idx}
              sectionId={sectionId}
              isReadOnly={isReadOnly}
              isPreview={isPreview}
              isEditable={isEditable}
              language={language}
              onCancel={onCancel}
              onDelete={onClickThreeDotMenuDeleteTask}
              onEdit={onEdit}
              editProcedureResource={editProcedureResource}
              handleResourceOrQuizPreview={handleResourceOrQuizPreview}
              setActiveSectionId={setActiveSectionId}
              taskUpdated={isTaskUpdated}
            />
          ),
        )}
      </div>

      {showAddTask && (
        <AddTask
          isInSection
          plan={plan}
          sectionId={sectionId}
          setShowAddTask={setShowAddTask}
        />
      )}
      {!isPreview && !isReadOnly && (
        <SectionButtons
          addProcedure={addProcedure}
          addQuiz={addQuiz as () => void}
          setShowAddTask={setShowAddTask}
          showAddTask={showAddTask}
        />
      )}
    </Card>
  );
};

export default Section;
